import React, { useState, useEffect, useRef } from 'react';
import { 
  Building2, Users, Target, DollarSign, TrendingUp, Building, 
  MapPin, Palette, HeartHandshake, Globe2, Save, Loader2, Sparkles,
  CheckCircle, BarChart3, BookOpen, Heart, Leaf, PawPrint, User, AlertCircle,
  PartyPopper
} from 'lucide-react';
import { COLORS, US_STATES, COUNTRIES, INDUSTRIES } from '../../../constants/dashboard';
import ProgressRing from '../shared/ProgressRing';
import CustomDomainView from './CustomDomainView';

const CATEGORIES = [
  { id: 'education', label: 'Education', Icon: BookOpen },
  { id: 'health', label: 'Health', Icon: Heart },
  { id: 'environment', label: 'Environment', Icon: Leaf },
  { id: 'animals', label: 'Animals', Icon: PawPrint },
  { id: 'human_services', label: 'Human Services', Icon: Users },
  { id: 'arts_culture', label: 'Arts & Culture', Icon: Palette },
  { id: 'youth', label: 'Youth', Icon: User },
];

const GEOGRAPHIC_OPTIONS = [
  { id: 'local', label: 'Local (City/Region)' },
  { id: 'state', label: 'State-wide' },
  { id: 'national', label: 'National (USA)' },
  { id: 'international', label: 'International/Global' },
];

const ESG_OPTIONS = [
  { id: 'environmental', label: 'Environmental', description: 'Climate, pollution, resources' },
  { id: 'social', label: 'Social', description: 'Labor, community, diversity' },
  { id: 'governance', label: 'Governance', description: 'Ethics, transparency, compliance' },
  { id: 'carbon_neutral', label: 'Carbon Neutral', description: 'Net-zero emissions target' },
  { id: 'dei', label: 'DEI Focus', description: 'Diversity, equity & inclusion' },
  { id: 'supply_chain', label: 'Supply Chain', description: 'Sustainable sourcing' },
];

const CompanyProfileView = ({ 
  activeSection = 'company',
  profileForm, 
  setProfileForm, 
  saveCompanyProfile, 
  savingProfile, 
  stats, 
  campaigns, 
  users,
  businessProfile 
}) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const [showSavePrompt, setShowSavePrompt] = useState(false);
  const prevCompletionRef = useRef(0);

  const profileCompletion = Math.min(
    ((profileForm.name ? 10 : 0) +
    (profileForm.email ? 10 : 0) +
    (profileForm.industry ? 15 : 0) +
    (profileForm.address_city ? 10 : 0) +
    (profileForm.logo_url || profileForm.mission_statement ? 10 : 0) +
    ((profileForm.target_causes?.length || 0) > 0 ? 25 : 0) +
    ((profileForm.geographic_focus?.length || 0) > 0 ? 20 : 0)), 100
  );

  useEffect(() => {
    if (profileCompletion === 100 && prevCompletionRef.current < 100) {
      setShowSavePrompt(true);
    }
    prevCompletionRef.current = profileCompletion;
  }, [profileCompletion]);

  const REQUIRED_FIELDS = {
    name: 'Company Name',
    industry: 'Industry',
    email: 'Email',
    phone_number: 'Phone',
    website: 'Website',
    employee_count: 'Employee Count',
    country: 'Country',
    address_street: 'Street Address',
    address_city: 'City',
    address_state: 'State/Province',
    address_zip: 'ZIP/Postal Code',
    logo_url: 'Logo URL',
    mission_statement: 'Mission Statement'
  };

  const validateForm = () => {
    const errors = {};
    Object.entries(REQUIRED_FIELDS).forEach(([field, label]) => {
      if (!profileForm[field] || profileForm[field].trim() === '') {
        errors[field] = `${label} is required`;
      }
    });
    if (!profileForm.target_causes || profileForm.target_causes.length === 0) {
      errors.target_causes = 'Select at least one CSR focus area';
    }
    if (!profileForm.geographic_focus || profileForm.geographic_focus.length === 0) {
      errors.geographic_focus = 'Select at least one geographic focus';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveProfile = () => {
    if (validateForm()) {
      setShowValidationAlert(false);
      saveCompanyProfile();
    } else {
      setShowValidationAlert(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getInputClass = (fieldName, baseClass = '') => {
    const hasError = validationErrors[fieldName];
    return `w-full px-3 py-2 border ${hasError ? 'border-red-400 bg-red-50' : 'border-gray-200'} rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${baseClass}`;
  };

  // Reusable Profile Completion sidebar
  const renderProfileCompletion = () => (
    <div className="bg-white rounded-xl shadow-sm border p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Profile Completion</h3>
        <Sparkles className="w-4 h-4 text-amber-500" />
      </div>
      <div className="flex items-center gap-4 mb-3">
        <div className="relative flex-shrink-0">
          <ProgressRing 
            progress={profileCompletion} 
            size={70} 
            strokeWidth={7} 
            color={profileCompletion === 100 ? '#38BDF8' : '#F59E0B'} 
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <span className={`text-sm font-bold ${profileCompletion === 100 ? 'text-emerald-700' : 'text-amber-700'}`}>
              {profileCompletion}%
            </span>
          </div>
        </div>
        <div className="space-y-1 text-xs">
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-3 h-3 ${profileForm.name ? 'text-green-500' : 'text-gray-300'}`} />
            <span className={profileForm.name ? 'text-gray-700' : 'text-gray-400'}>Company Name</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-3 h-3 ${profileForm.industry ? 'text-green-500' : 'text-gray-300'}`} />
            <span className={profileForm.industry ? 'text-gray-700' : 'text-gray-400'}>Industry</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-3 h-3 ${profileForm.address_city ? 'text-green-500' : 'text-gray-300'}`} />
            <span className={profileForm.address_city ? 'text-gray-700' : 'text-gray-400'}>Location</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-3 h-3 ${profileForm.logo_url || profileForm.mission_statement ? 'text-green-500' : 'text-gray-300'}`} />
            <span className={profileForm.logo_url || profileForm.mission_statement ? 'text-gray-700' : 'text-gray-400'}>Branding</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-3 h-3 ${(profileForm.target_causes?.length || 0) > 0 ? 'text-green-500' : 'text-gray-300'}`} />
            <span className={(profileForm.target_causes?.length || 0) > 0 ? 'text-gray-700' : 'text-gray-400'}>CSR Focus</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle className={`w-3 h-3 ${(profileForm.geographic_focus?.length || 0) > 0 ? 'text-green-500' : 'text-gray-300'}`} />
            <span className={(profileForm.geographic_focus?.length || 0) > 0 ? 'text-gray-700' : 'text-gray-400'}>Geographic</span>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-2 pt-2 border-t border-gray-100">
        <Sparkles className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
        <div>
          <p className="text-xs font-medium text-amber-700 mb-1">AI-Powered Matching</p>
          <p className="text-xs text-gray-500 leading-relaxed">
            Complete your profile to get personalized charity recommendations aligned with your industry, location, and CSR goals.
          </p>
        </div>
      </div>
    </div>
  );

  // Reusable Save header button
  const renderSaveButton = () => (
    <button 
      data-testid="settings-save-btn"
      onClick={handleSaveProfile}
      disabled={savingProfile}
      className="px-3 py-1.5 text-white text-xs rounded-lg font-medium flex items-center gap-1"
      style={{ background: COLORS.primary }}
    >
      {savingProfile ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
      {savingProfile ? 'Saving...' : 'Save'}
    </button>
  );

  return (
    <div className="p-4" style={{ background: COLORS.background }}>
      {/* Validation Alert */}
      {showValidationAlert && Object.keys(validationErrors).length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-semibold text-red-800 text-sm">Please complete all required fields</h4>
            <ul className="mt-1 text-xs text-red-600 space-y-0.5">
              {Object.values(validationErrors).map((error, idx) => (
                <li key={idx}>&#8226; {error}</li>
              ))}
            </ul>
          </div>
          <button 
            onClick={() => setShowValidationAlert(false)}
            className="ml-auto text-red-400 hover:text-red-600"
          >
            &#215;
          </button>
        </div>
      )}

      {/* 100% Profile Complete Save Prompt */}
      {showSavePrompt && profileCompletion === 100 && (
        <div 
          data-testid="profile-save-prompt"
          className="mb-4 p-4 rounded-xl border-2 border-emerald-300 flex items-center gap-4"
          style={{ background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)' }}
        >
          <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
            <PartyPopper className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-emerald-900 text-sm">Your profile is 100% complete!</h4>
            <p className="text-xs text-emerald-700 mt-0.5">Save now to unlock AI-powered charity matching and personalized recommendations.</p>
          </div>
          <button
            data-testid="profile-save-prompt-btn"
            onClick={() => { handleSaveProfile(); setShowSavePrompt(false); }}
            disabled={savingProfile}
            className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg flex items-center gap-2 transition-all shadow-md hover:shadow-lg flex-shrink-0"
          >
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {savingProfile ? 'Saving...' : 'Save Profile'}
          </button>
          <button 
            onClick={() => setShowSavePrompt(false)}
            className="text-emerald-400 hover:text-emerald-600 flex-shrink-0"
          >
            &#215;
          </button>
        </div>
      )}

      {/* ===== COMPANY INFO & LOCATION SECTION ===== */}
      {activeSection === 'company' && (
        <>
          {/* Stats Row */}
          <div className="grid grid-cols-4 gap-3 mb-4" data-testid="settings-stats-row">
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Building2 className="w-5 h-5" style={{ color: COLORS.primary }} />
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">Active</span>
              </div>
              <p className="text-lg font-bold" style={{ color: COLORS.accent }}>{profileForm.name || 'Company'}</p>
              <p className="text-xs text-gray-500">{profileForm.industry || 'Industry'}</p>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Users className="w-5 h-5 text-purple-500" />
                <TrendingUp className="w-3 h-3 text-green-500" />
              </div>
              <p className="text-lg font-bold text-purple-600">{users.length}</p>
              <p className="text-xs text-gray-500">Team Members</p>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-5 h-5" style={{ color: COLORS.secondary }} />
                <span className="text-xs text-gray-500">{new Date().getFullYear()}</span>
              </div>
              <p className="text-lg font-bold" style={{ color: COLORS.secondary }}>{campaigns.length}</p>
              <p className="text-xs text-gray-500">Campaigns</p>
            </div>
            <div className="bg-white rounded-xl p-4 border shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                <TrendingUp className="w-3 h-3 text-green-500" />
              </div>
              <p className="text-lg font-bold text-green-600">${(stats?.total_raised || 0).toLocaleString()}</p>
              <p className="text-xs text-gray-500">Total Given</p>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-8 space-y-4">
              {/* Company Information */}
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4" style={{ color: COLORS.primary }} />
                    <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Company Information</h3>
                  </div>
                  {renderSaveButton()}
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Company Name <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={profileForm.name}
                        onChange={(e) => setProfileForm({...profileForm, name: e.target.value})}
                        className={getInputClass('name')}
                        placeholder="Enter company name"
                      />
                      {validationErrors.name && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.name}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Industry <span className="text-red-500">*</span></label>
                      <select
                        value={profileForm.industry || ''}
                        onChange={(e) => setProfileForm({...profileForm, industry: e.target.value})}
                        className={getInputClass('industry')}
                      >
                        {INDUSTRIES.map(ind => (
                          <option key={ind.value} value={ind.value}>{ind.label}</option>
                        ))}
                      </select>
                      {validationErrors.industry && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.industry}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Email <span className="text-red-500">*</span></label>
                      <input
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({...profileForm, email: e.target.value})}
                        className={getInputClass('email')}
                        placeholder="contact@company.com"
                      />
                      {validationErrors.email && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Phone <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={profileForm.phone_number || ''}
                        onChange={(e) => setProfileForm({...profileForm, phone_number: e.target.value})}
                        className={getInputClass('phone_number')}
                        placeholder="(555) 123-4567"
                      />
                      {validationErrors.phone_number && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.phone_number}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Website <span className="text-red-500">*</span></label>
                      <input
                        type="url"
                        value={profileForm.website || ''}
                        onChange={(e) => setProfileForm({...profileForm, website: e.target.value})}
                        className={getInputClass('website')}
                        placeholder="https://company.com"
                      />
                      {validationErrors.website && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.website}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Employee Count <span className="text-red-500">*</span></label>
                      <select
                        value={profileForm.employee_count || ''}
                        onChange={(e) => setProfileForm({...profileForm, employee_count: e.target.value})}
                        className={getInputClass('employee_count')}
                      >
                        <option value="">Select range</option>
                        <option value="1-50">1-50</option>
                        <option value="51-200">51-200</option>
                        <option value="201-500">201-500</option>
                        <option value="501-1000">501-1000</option>
                        <option value="1000+">1000+</option>
                      </select>
                      {validationErrors.employee_count && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.employee_count}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center gap-2" style={{ background: COLORS.light }}>
                  <MapPin className="w-4 h-4 text-green-500" />
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Location <span className="text-red-500">*</span></h3>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Country <span className="text-red-500">*</span></label>
                      <select
                        value={profileForm.country || ''}
                        onChange={(e) => setProfileForm({...profileForm, country: e.target.value, address_state: ''})}
                        className={getInputClass('country')}
                      >
                        <option value="">Select country...</option>
                        {COUNTRIES.map(c => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                      {validationErrors.country && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.country}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Street Address <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={profileForm.address_street || ''}
                        onChange={(e) => setProfileForm({...profileForm, address_street: e.target.value})}
                        className={getInputClass('address_street')}
                        placeholder="123 Main Street"
                      />
                      {validationErrors.address_street && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.address_street}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">City <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={profileForm.address_city || ''}
                        onChange={(e) => setProfileForm({...profileForm, address_city: e.target.value})}
                        className={getInputClass('address_city')}
                        placeholder="City"
                      />
                      {validationErrors.address_city && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.address_city}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">State/Province <span className="text-red-500">*</span></label>
                      {profileForm.country === 'US' ? (
                        <select
                          value={profileForm.address_state || ''}
                          onChange={(e) => setProfileForm({...profileForm, address_state: e.target.value})}
                          className={getInputClass('address_state')}
                        >
                          <option value="">Select State</option>
                          {US_STATES.map(s => (
                            <option key={s.code} value={s.code}>{s.name}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={profileForm.address_state || ''}
                          onChange={(e) => setProfileForm({...profileForm, address_state: e.target.value})}
                          className={getInputClass('address_state')}
                          placeholder="State / Province / Region"
                        />
                      )}
                      {validationErrors.address_state && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.address_state}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{profileForm.country === 'US' ? 'ZIP Code' : 'Postal Code'} <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        value={profileForm.address_zip || ''}
                        onChange={(e) => setProfileForm({...profileForm, address_zip: e.target.value})}
                        className={getInputClass('address_zip')}
                        placeholder={profileForm.country === 'US' ? 'ZIP Code' : 'Postal Code'}
                      />
                      {validationErrors.address_zip && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.address_zip}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Sidebar */}
            <div className="col-span-4 space-y-4">
              {renderProfileCompletion()}

              {/* Impact Overview */}
              <div className="bg-white rounded-xl shadow-sm border p-4" data-testid="impact-overview">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Impact Overview</h3>
                  <BarChart3 className="w-4 h-4" style={{ color: COLORS.primary }} />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Total Donated</span>
                    <span className="text-sm font-bold text-green-600" data-testid="total-donated">${(stats?.total_raised || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Company Match</span>
                    <span className="text-sm font-bold" style={{ color: COLORS.secondary }} data-testid="company-match">${(stats?.total_raised || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Lives Impacted</span>
                    <span className="text-sm font-bold text-pink-600" data-testid="lives-impacted">{(stats?.total_impact || 0).toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-600">Charities Supported</span>
                    <span className="text-sm font-bold" style={{ color: COLORS.primary }} data-testid="charities-supported">{campaigns.length}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ===== BRANDING & CSR SECTION ===== */}
      {activeSection === 'branding' && (
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-8 space-y-4">
            {/* Branding */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
                <div className="flex items-center gap-2">
                  <Palette className="w-4 h-4 text-purple-500" />
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Branding <span className="text-red-500">*</span></h3>
                </div>
                {renderSaveButton()}
              </div>
              <div className="p-4 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Logo URL <span className="text-red-500">*</span></label>
                  <input
                    type="url"
                    value={profileForm.logo_url || ''}
                    onChange={(e) => setProfileForm({...profileForm, logo_url: e.target.value})}
                    className={getInputClass('logo_url')}
                    placeholder="https://yourcompany.com/logo.png"
                  />
                  {validationErrors.logo_url && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.logo_url}</p>}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Mission Statement <span className="text-red-500">*</span></label>
                  <textarea
                    value={profileForm.mission_statement || ''}
                    onChange={(e) => setProfileForm({...profileForm, mission_statement: e.target.value})}
                    className={getInputClass('mission_statement', 'h-16 resize-none')}
                    placeholder="Our mission is to..."
                  />
                  {validationErrors.mission_statement && <p className="text-[10px] text-red-500 mt-0.5">{validationErrors.mission_statement}</p>}
                </div>
              </div>
            </div>

            {/* CSR Focus & Geographic Focus */}
            <div className="grid grid-cols-2 gap-4">
              {/* CSR Focus */}
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
                  <div className="flex items-center gap-2">
                    <HeartHandshake className="w-4 h-4 text-pink-500" />
                    <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>CSR Focus Areas <span className="text-red-500">*</span></h3>
                  </div>
                  <span className="text-xs text-gray-500">
                    {(profileForm.target_causes || []).filter(c => CATEGORIES.some(cat => cat.id === c)).length} selected
                  </span>
                </div>
                <div className="p-4">
                  {validationErrors.target_causes && (
                    <p className="text-xs text-red-500 mb-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {validationErrors.target_causes}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {CATEGORIES.map(cat => (
                      <button
                        key={cat.id}
                        onClick={() => {
                          const validCauses = (profileForm.target_causes || []).filter(c => CATEGORIES.some(cat2 => cat2.id === c));
                          if (validCauses.includes(cat.id)) {
                            setProfileForm({...profileForm, target_causes: validCauses.filter(c => c !== cat.id)});
                          } else {
                            setProfileForm({...profileForm, target_causes: [...validCauses, cat.id]});
                          }
                        }}
                        className={`p-2 rounded-lg text-xs font-medium flex items-center gap-2 transition-all ${
                          (profileForm.target_causes || []).includes(cat.id)
                            ? 'bg-blue-100 text-blue-700 border-2 border-blue-300'
                            : validationErrors.target_causes
                              ? 'bg-red-50 text-gray-600 border-2 border-red-200 hover:bg-red-100'
                              : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                        }`}
                      >
                        <cat.Icon className="w-4 h-4" />
                        {cat.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Geographic Focus */}
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
                  <div className="flex items-center gap-2">
                    <Globe2 className="w-4 h-4 text-cyan-500" />
                    <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Geographic Focus <span className="text-red-500">*</span></h3>
                  </div>
                  <span className="text-xs text-gray-500">
                    {(profileForm.geographic_focus || []).filter(g => GEOGRAPHIC_OPTIONS.some(opt => opt.id === g)).length} selected
                  </span>
                </div>
                <div className="p-4">
                  {validationErrors.geographic_focus && (
                    <p className="text-xs text-red-500 mb-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {validationErrors.geographic_focus}
                    </p>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    {GEOGRAPHIC_OPTIONS.map(geo => (
                      <button
                        key={geo.id}
                        onClick={() => {
                          const validFocus = (profileForm.geographic_focus || []).filter(g => GEOGRAPHIC_OPTIONS.some(opt => opt.id === g));
                          if (validFocus.includes(geo.id)) {
                            setProfileForm({...profileForm, geographic_focus: validFocus.filter(g => g !== geo.id)});
                          } else {
                            setProfileForm({...profileForm, geographic_focus: [...validFocus, geo.id]});
                          }
                        }}
                        className={`p-2 rounded-lg text-xs font-medium text-left transition-all ${
                          (profileForm.geographic_focus || []).includes(geo.id)
                            ? 'bg-cyan-100 text-cyan-700 border-2 border-cyan-300'
                            : validationErrors.geographic_focus
                              ? 'bg-red-50 text-gray-600 border-2 border-red-200 hover:bg-red-100'
                              : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                        }`}
                      >
                        {geo.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* ESG Focus Areas */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
                <div className="flex items-center gap-2">
                  <Leaf className="w-4 h-4 text-green-500" />
                  <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>ESG Focus Areas</h3>
                </div>
                <span className="text-xs text-gray-500">
                  {(profileForm.esg_priorities || []).length} selected
                </span>
              </div>
              <div className="p-4">
                <p className="text-xs text-gray-500 mb-3">Select your company&apos;s ESG priorities to help align with relevant charities and campaigns.</p>
                <div className="grid grid-cols-3 gap-2">
                  {ESG_OPTIONS.map(esg => (
                    <button
                      key={esg.id}
                      onClick={() => {
                        const current = profileForm.esg_priorities || [];
                        if (current.includes(esg.id)) {
                          setProfileForm({...profileForm, esg_priorities: current.filter(e => e !== esg.id)});
                        } else {
                          setProfileForm({...profileForm, esg_priorities: [...current, esg.id]});
                        }
                      }}
                      className={`p-3 rounded-lg text-left transition-all ${
                        (profileForm.esg_priorities || []).includes(esg.id)
                          ? 'bg-green-100 text-green-700 border-2 border-green-300'
                          : 'bg-gray-50 text-gray-600 border-2 border-transparent hover:bg-gray-100'
                      }`}
                    >
                      <p className="text-sm font-medium">{esg.label}</p>
                      <p className="text-xs opacity-75">{esg.description}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="col-span-4 space-y-4">
            {renderProfileCompletion()}
          </div>
        </div>
      )}

      {/* ===== IMPACT PORTAL SECTION ===== */}
      {activeSection === 'portal' && (
        <div className="max-w-4xl">
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between" style={{ background: COLORS.light }}>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-500" />
                <h3 className="font-semibold text-sm" style={{ color: COLORS.accent }}>Impact Portal Settings</h3>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">Donation Portal</span>
                {renderSaveButton()}
              </div>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-xs text-gray-500">
                Configure how employees are identified and whether guests can donate on your public Impact Portal.
              </p>
              
              {/* Guest Donations Toggle */}
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-700">Allow Guest Donations</p>
                  <p className="text-xs text-gray-500">Non-employees can donate to your campaigns</p>
                </div>
                <button
                  onClick={() => setProfileForm({...profileForm, guest_donations_enabled: !profileForm.guest_donations_enabled})}
                  className={`w-12 h-6 rounded-full transition-all ${profileForm.guest_donations_enabled !== false ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-all ${profileForm.guest_donations_enabled !== false ? 'translate-x-6' : 'translate-x-0.5'}`}></div>
                </button>
              </div>
              
              {/* Employee Detection Methods */}
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Employee Detection Methods</p>
                <div className="space-y-2">
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={(profileForm.employee_detection_methods || ['domain']).includes('domain')}
                      onChange={(e) => {
                        const methods = profileForm.employee_detection_methods || ['domain'];
                        if (e.target.checked) {
                          setProfileForm({...profileForm, employee_detection_methods: [...methods, 'domain']});
                        } else {
                          setProfileForm({...profileForm, employee_detection_methods: methods.filter(m => m !== 'domain')});
                        }
                      }}
                      className="w-4 h-4 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Email Domain Matching</p>
                      <p className="text-xs text-gray-500">Detect employees by email domain (e.g., @yourcompany.com)</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                    <input
                      type="checkbox"
                      checked={(profileForm.employee_detection_methods || []).includes('invite')}
                      onChange={(e) => {
                        const methods = profileForm.employee_detection_methods || [];
                        if (e.target.checked) {
                          setProfileForm({...profileForm, employee_detection_methods: [...methods, 'invite']});
                        } else {
                          setProfileForm({...profileForm, employee_detection_methods: methods.filter(m => m !== 'invite')});
                        }
                      }}
                      className="w-4 h-4 rounded"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-700">Invite-Only</p>
                      <p className="text-xs text-gray-500">Only pre-registered team members are detected as employees</p>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Allowed Email Domains */}
              {(profileForm.employee_detection_methods || ['domain']).includes('domain') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Allowed Email Domains</label>
                  <input
                    type="text"
                    value={(profileForm.allowed_email_domains || []).join(', ')}
                    onChange={(e) => setProfileForm({
                      ...profileForm, 
                      allowed_email_domains: e.target.value.split(',').map(d => d.trim().toLowerCase()).filter(d => d)
                    })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                    placeholder="acmecorp.com, acme.co"
                  />
                  <p className="text-xs text-gray-400 mt-1">Comma-separated list of email domains</p>
                </div>
              )}
              
              {/* Portal Link */}
              <div className="p-3 bg-indigo-50 rounded-lg">
                <p className="text-xs text-indigo-600 font-medium mb-1">Your Impact Portal URL</p>
                <code className="text-sm text-indigo-800 break-all">
                  {window.location.origin}/impact/{businessProfile?.business_id || 'your-company'}
                </code>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ===== WHITE LABEL DOMAIN SECTION ===== */}
      {activeSection === 'domain' && (
        <div className="max-w-4xl">
          <CustomDomainView businessProfile={businessProfile} />
        </div>
      )}
    </div>
  );
};

export default CompanyProfileView;
